import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { Plus, Mail, Phone } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { Switch } from '../../components/ui/Switch';
import { Popover, PopoverContent } from '../../components/ui/Popover';
import { CalendarReservationContentMode } from '../../models/owner-portal/calendarEvents';
import { COIModel, coiRecordType } from '../../models/owner-portal/listingGeneral';
import {
  useGetRTKQListingOrderItemsQuery,
  useGetRTKQReservationsQuery,
  useGetRTKQSingleListingQuery,
  useGetRTKQTasksQuery
} from '../../redux/rtkQuery/apiSlice';
import { PATH_HOME } from '../../routes/paths';
import ListingDetailsCard from '../../sections/dashboard/listing/ListingDetailsCard';
import { ReservationTable } from '../../sections/tables/reservation/ReservationTable';
import TaskTable from '../../sections/tables/task/TaskTable';
import fTitle from '../../utils/owner-portal/formatTitle';
import {
  escapeButtonLabel,
  showTableTabCounts,
  TAB_BADGE_STYLES_BY_INDEX
} from '../../utils/owner-portal/uiConstants';
import { TASK_TAB_OPTION, TASK_TAB_OPTIONS } from '../../utils/owner-portal/taskConstants';
import ListingCOICard from '../../sections/dashboard/listing/list/Page/ListingCOICard';
import { heatMapDateEnd, heatMapDateStart, useHeatmapDates } from '../../hooks/useHeatmapDates';
import { brandConfig } from '../../config';
import { ChartHeatMapYear } from '../../sections/_examples/extra/chart';
import { HeatmapLoading } from '../../components/loading/HeatmapLoading';
// ----------------------------------------------------------------------

export default function ListingDetailsPage() {
  const { id: listingId } = useParams();

  const [openSupportPopOver, setOpenSupportPopOver] = useState(false);
  const [filterTaskTab, setFilterTaskTab] = useState(TASK_TAB_OPTION.loi);
  const [channelLogoErrors, setChannelLogoErrors] = useState<Set<string>>(() => new Set());
  const handleFilterTaskTab = (value: string) => setFilterTaskTab(value as TASK_TAB_OPTION);
  const [startDate, endDate] = useHeatmapDates();

  const { data: tasks } = useGetRTKQTasksQuery({ listingId: listingId ?? undefined });
  const { data: lois } = useGetRTKQListingOrderItemsQuery({ listingId: listingId ?? undefined });
  const taskTabInfo = useMemo(() => {
    if (!showTableTabCounts) return null;
    const taskCount = tasks?.length ?? 0;
    const loiCount = lois?.length ?? 0;
    return [
      {
        value: TASK_TAB_OPTION.loi,
        label: TASK_TAB_OPTION.loi,
        color: 'primary' as const,
        count: loiCount
      },
      {
        value: TASK_TAB_OPTION.task,
        label: TASK_TAB_OPTION.task,
        color: 'default' as const,
        count: taskCount
      }
    ];
  }, [tasks, lois]);

  const {
    data: listing,
    isFetching: isFetchingListing,
    error: _listingError,
    isUninitialized: _isUninitialized,
    isSuccess
  } = useGetRTKQSingleListingQuery({ listingId: listingId });

  const {
    data: heatMapReservationData,
    error: _heatMapReservationsError,
    isFetching: isFetchingHeatMap,
    isSuccess: isSuccessHeatMap,
    isError: _isErrorHeatMap
  } = useGetRTKQReservationsQuery({
    listingId: listingId,
    startDate: heatMapDateStart,
    endDate: heatMapDateEnd
  });

  const heatMapTableData = useMemo(() => {
    if (!isSuccessHeatMap || !heatMapReservationData) {
      return [];
    }
    // Data is already ReservationGeneral instances, no need to transform
    return heatMapReservationData;
  }, [heatMapReservationData, isSuccessHeatMap]);

  const integrations = useMemo(() => {
    if (isSuccess && listing.integrations) {
      return listing.integrations;
    }
    return [];
  }, [listing, isSuccess]);

  let listingCOIModels: null | COIModel[] = null;

  if (isSuccess && listing) {
    if (listing.management_items.length > 0) {
      listingCOIModels = listing.management_items.filter((mgmtItem) => {
        if (mgmtItem.type !== coiRecordType) {
          return false;
        }

        return true;
      });
    }
  }

  return (
    <>
      <Helmet>
        <title>
          {fTitle(`Property${listing?.name_detailed ? ': ' + listing.name_detailed : ''}`)}
        </title>
      </Helmet>

      <div className="mx-auto w-full max-w-6xl px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Property Details
          </h1>
          {listingId && (
            <Button asChild className="shrink-0">
              <Link to={PATH_HOME.booking.createFromListing(listingId)}>
                Book This Property
                <Plus className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-6">
              {listingId && (
                <ListingDetailsCard
                  listingId={listingId}
                  handleOpenSupportPopover={() => setOpenSupportPopOver(true)}
                />
              )}
              {listingCOIModels !== null && (
                <div className="space-y-4">
                  {listingCOIModels.map((coi, i) => {
                    return (
                      <div key={`coi${i}`}>
                        <ListingCOICard model={coi} modelLoading={isFetchingListing} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="lg:col-span-4">
              {integrations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Listing Channels</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {integrations.map((integration, i) => {
                        const isWebsite = integration.channel_name === 'Website';
                        const intergarationImageSrc = isWebsite
                          ? `/assets/icons/brands/ic_brand_website_${brandConfig.brandCode.toLowerCase()}.svg`
                          : `/assets/icons/brands/ic_brand_${integration.channel_name.toLowerCase()}.svg`;
                        const logoFailed = channelLogoErrors.has(integration.channel_name);
                        const canOpen = Boolean(integration.is_active && integration.url);
                        return (
                          <div
                            key={`integration_${i}`}
                            className={`flex w-full items-center justify-between gap-2 p-2 rounded-md transition-colors ${
                              canOpen ? 'hover:bg-accent' : 'opacity-50'
                            }`}
                          >
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center text-left disabled:cursor-default"
                              onClick={() => {
                                if (canOpen && integration.url) {
                                  window.open(integration.url, '_blank');
                                }
                              }}
                              disabled={!canOpen}
                            >
                              {logoFailed ? (
                                <span className="h-8 flex items-center text-sm font-medium text-foreground">
                                  {integration.channel_name}
                                </span>
                              ) : (
                                <img
                                  className="h-8 w-auto max-w-[100px] lg:max-w-[200px] dark:invert"
                                  alt={integration.channel_name}
                                  src={intergarationImageSrc}
                                  onError={() => {
                                    setChannelLogoErrors((prev) =>
                                      new Set(prev).add(integration.channel_name)
                                    );
                                  }}
                                />
                              )}
                            </button>
                            <Switch checked={integration.is_active} disabled />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
          {/* Booking Heatmap */}
          <div className="mb-6">
            {isFetchingHeatMap ? (
              <Card>
                <CardHeader>
                  <CardTitle>Booking Heatmap</CardTitle>
                </CardHeader>
                <CardContent>
                  <HeatmapLoading />
                </CardContent>
              </Card>
            ) : isSuccessHeatMap && heatMapTableData.length > 0 ? (
              <ChartHeatMapYear
                reservations={heatMapTableData}
                startDate={startDate}
                endDate={endDate}
                fillWidth
              />
            ) : null}
          </div>

          {/* Confirmed Reservations */}
          <div>
            <div className="mb-3">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                Confirmed Reservations
              </h3>
            </div>
            <Card>
              <CardContent className="p-0">
                <ReservationTable
                  listingId={listingId}
                  mode={CalendarReservationContentMode.SingleListing}
                />
              </CardContent>
            </Card>
          </div>
          <div>
            <div className="mb-3">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                Work Orders & Tasks
              </h3>
            </div>
            <Tabs value={filterTaskTab} onValueChange={handleFilterTaskTab}>
              <TabsList variant="default" className="mb-4">
                {!showTableTabCounts
                  ? TASK_TAB_OPTIONS.map((tab: string) => (
                      <TabsTrigger key={tab} value={tab} variant="default" className="capitalize">
                        {tab}
                      </TabsTrigger>
                    ))
                  : taskTabInfo?.map((tab, i) => {
                      const badgeStyle = TAB_BADGE_STYLES_BY_INDEX[Math.min(i, 4)];
                      return (
                        <TabsTrigger
                          key={`${tab.value}-${i}`}
                          value={tab.value}
                          variant="default"
                          className="capitalize"
                        >
                          <Badge variant={badgeStyle.variant} className={badgeStyle.className}>
                            {tab.count}
                          </Badge>
                          {tab.label}
                        </TabsTrigger>
                      );
                    })}
              </TabsList>
            </Tabs>
            <Card>
              <CardContent className="p-0">
                <TaskTable
                  listingId={listingId}
                  filterTab={filterTaskTab}
                  onFilterTabChange={handleFilterTaskTab}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-4 lg:hidden">
          <Button variant="ghost" asChild>
            <Link to="/">{escapeButtonLabel}</Link>
          </Button>
        </div>
      </div>

      {/* Support Popover */}
      {isSuccess && (
        <Popover open={openSupportPopOver} onOpenChange={setOpenSupportPopOver}>
          <PopoverContent className="w-auto" align="start">
            <div className="space-y-2">
              <a
                href={`mailto:${listing?.owner_email}?subject=${listing?.unit_id} Support`}
                onClick={() => setOpenSupportPopOver(false)}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent text-primary dark:text-secondary transition-colors"
              >
                <Mail className="h-4 w-4" />
                {listing?.owner_email}
              </a>
              <a
                href={`tel:${listing?.owner_phone}`}
                onClick={() => setOpenSupportPopOver(false)}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent text-primary dark:text-secondary transition-colors"
              >
                <Phone className="h-4 w-4" />
                {listing?.owner_phone}
              </a>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
